/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import * as components from "../components/index.js";
import { TeralError } from "./teralerror.js";

/**
 * Error response when the requested resource cannot be found. This occurs when:
 *
 * @remarks
 * - The specified resource ID doesn't exist in your workspace
 * - The resource has been deleted or moved
 * - The resource exists but is not accessible with current permissions
 *
 * To resolve this error, verify the resource ID is correct and that you have access to it.
 */
export type NotFoundErrorResponseData = {
  /**
   * Metadata object included in every API response. This provides context about the request and is essential for debugging, audit trails, and support inquiries. The `requestId` is particularly important when troubleshooting issues with the Teral support team.
   */
  meta: components.Meta;
  /**
   * Base error structure following Problem Details for HTTP APIs (RFC 7807). This provides a standardized way to carry machine-readable details of errors in HTTP response content.
   */
  error: components.BaseError;
};

/**
 * Error response when the requested resource cannot be found. This occurs when:
 *
 * @remarks
 * - The specified resource ID doesn't exist in your workspace
 * - The resource has been deleted or moved
 * - The resource exists but is not accessible with current permissions
 *
 * To resolve this error, verify the resource ID is correct and that you have access to it.
 */
export class NotFoundErrorResponse extends TeralError {
  /**
   * Metadata object included in every API response. This provides context about the request and is essential for debugging, audit trails, and support inquiries. The `requestId` is particularly important when troubleshooting issues with the Teral support team.
   */
  meta: components.Meta;
  /**
   * Base error structure following Problem Details for HTTP APIs (RFC 7807). This provides a standardized way to carry machine-readable details of errors in HTTP response content.
   */
  error: components.BaseError;

  /** The original data that was passed to this error instance. */
  data$: NotFoundErrorResponseData;

  constructor(
    err: NotFoundErrorResponseData,
    httpMeta: { response: Response; request: Request; body: string },
  ) {
    const message = "message" in err && typeof err.message === "string"
      ? err.message
      : `API error occurred: ${JSON.stringify(err)}`;
    super(message, httpMeta);
    this.data$ = err;
    this.meta = err.meta;
    this.error = err.error;

    this.name = "NotFoundErrorResponse";
  }
}

/** @internal */
export const NotFoundErrorResponse$inboundSchema: z.ZodType<
  NotFoundErrorResponse,
  z.ZodTypeDef,
  unknown
> = z.object({
  meta: components.Meta$inboundSchema,
  error: components.BaseError$inboundSchema,
  request$: z.instanceof(Request),
  response$: z.instanceof(Response),
  body$: z.string(),
})
  .transform((v) => {
    return new NotFoundErrorResponse(v, {
      request: v.request$,
      response: v.response$,
      body: v.body$,
    });
  });
